<template>
  <div class="comment-container">
    <div class="comment-type-icon">
      <i :class="`bi-${typeDetail.icon}`" />
    </div>
    <div class="comment-content">
      <div class="comment-bar">
        <div class="comment-author">
          {{ authorName }}
        </div>
        <div class="comment-action">
          {{ typeDetail.action }}
        </div>
        <IconButton v-if="comment.isStandalone" icon="trash" @click="deleteComment" :button="false" small no-padding :busy="deleting" />
        <div class="comment-timestamp">
          <BTooltip :title="formatDateTime(comment.createdAt)">
            {{ formatDateFromNow(comment.createdAt) }}
          </BTooltip>
        </div>
      </div>
      <div v-if="source?.title" :class="['comment-source']">
        <i v-if="source.icon" :class="`bi-${source.icon}`"/>
        <span>{{ source.title }}</span>
      </div>
      <div v-if="messageLines.length > 0" class="comment-message">
        <div v-for="(line, index) in messageLines" :key="index">
          {{ line }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Comment from "@/models/ListingComment";
import BTooltip from "@/components/common/BTooltip.vue";
import IconButton from "@/components/common/IconButton.vue";

export default {
  components: {IconButton, BTooltip},
  props: {
    comment: Comment,
    source: {
      type: Object,
      default: null
    }
  },
  emits: ['deleted'],
  data() {
    return {
      deleting: false
    };
  },
  computed: {
    typeDetail() {
      if (this.comment.isStandalone) {
        return {
          icon: 'chat',
          label: 'Standalone Comment',
          action: 'added a comment'
        };
      } else if (this.comment.isDeferral) {
        return {
          icon: 'clock',
          label: 'Deferral',
          action: `${this.comment.metadata.action === 'deferred' ? 'deferred' : 'resumed'} ${this.source?.type ? `the ${this.source.type}` : 'this'}`
        };
      } else if (this.comment.isCorrespondence) {
        return {
          icon: 'envelope',
          label: 'Correspondence',
          action: this.comment.metadata.action === 'correspondence_added'
            ? 'created a letter'
            : this.comment.metadata.action === 'correspondence_updated'
            ? 'updated a letter'
            : 'canceled a letter'
        };
      } else if (this.comment.isVerification) {
        return {
          icon: 'check-circle',
          label: 'Listing Verification',
          action: `${this.comment.metadata.action === 'verified' ? 'verified' : 'cleared'} the listing`
        };
      } else if (this.comment.isLicenseEdit) {
        return {
          icon: 'pencil-square',
          label: 'License Edit',
          action: this.licenseEditAction
        };
      } else {
        return {
          icon: 'question',
          label: 'Unknown'
        };
      }
    },
    isLetterComment() {
      const action = this.comment.metadata?.action;
      return action === 'correspondence_added'
        || action === 'correspondence_updated'
        || action === 'correspondence_removed';
    },
    messageLines() {
      const message = this.comment.message || '';
      if (!message) {
        return [];
      }
      const normalizedMessage = this.isLetterComment
        ? message
          .replace(/\bCorrespondence\b/g, 'Letter')
          .replace(/\bcorrespondence\b/g, 'letter')
          .replace(/\bCorrespondences\b/g, 'Letters')
          .replace(/\bcorrespondences\b/g, 'letters')
        : message;
      return normalizedMessage.split(/\n/g);
    },
    authorName() {
      return this.comment.createdBy?.displayName || this.comment.createdBy?.email || 'Unknown user';
    },
    commentTypeLabel() {
      if (this.isLetterComment) return 'Letter';
      if (this.comment.isLicenseEdit) return 'License Edit';
      if (this.comment.isVerification) return 'Verification';
      if (this.comment.isDeferral) return 'Deferral';
      return 'Comment';
    },
    commentBadgeClass() {
      if (this.isLetterComment) return 'text-bg-info';
      if (this.comment.isLicenseEdit) return 'text-bg-dark';
      if (this.comment.isVerification) return 'text-bg-primary';
      if (this.comment.isDeferral) return 'text-bg-warning';
      return 'text-bg-secondary';
    },
    licenseEditAction() {
      const fields = Array.isArray(this.comment.metadata?.updatedFields)
        ? this.comment.metadata.updatedFields.filter(Boolean)
        : [];
      const fieldLabels = Array.isArray(this.comment.metadata?.updatedFieldLabels)
        ? this.comment.metadata.updatedFieldLabels.filter(Boolean)
        : [];
      const displayFields = fieldLabels.length === fields.length ? fieldLabels : fields;
      if (displayFields.length === 0) {
        return 'updated the license';
      }
      const display = displayFields
        .slice(0, 3)
        .join(', ');
      return displayFields.length > 3
        ? `updated ${display}, and ${displayFields.length - 3} more fields`
        : `updated ${display}`;
    },
    verificationSummary() {
      const action = this.comment.metadata?.action;
      const propertyId = this.comment.metadata?.verifiedPropertyId;
      if (action === 'cleared') {
        return 'Verification cleared';
      }
      if (propertyId) {
        return `Verified to property ${propertyId}`;
      }
      return 'Verification updated';
    },
    deferralSummary() {
      const action = this.comment.metadata?.action;
      const expiration = this.comment.metadata?.expiration;
      if (action === 'resumed') {
        return 'Deferral removed';
      }
      if (expiration) {
        return `Deferred until ${expiration}`;
      }
      return 'Deferred indefinitely';
    },
    letterSummary() {
      const action = this.comment.metadata?.action;
      const template = this.comment.metadata?.template;

      if (action === 'correspondence_added') {
        return template ? `Letter added (${template})` : 'Letter added';
      }
      if (action === 'correspondence_updated') {
        return template ? `Letter updated (${template})` : 'Letter updated';
      }
      if (action === 'correspondence_removed') {
        return template ? `Letter removed (${template})` : 'Letter removed';
      }
      return 'Letter activity';
    }
  },
  methods: {
    async deleteComment() {
      this.deleting = true;
      try {
        await this.comment.delete();
        this.$emit('deleted');
      } finally {
        this.deleting = false;
      }
    }
  }
}
</script>

<style scoped>
.comment-container {
  display: flex;
  flex-direction: row;
  gap: 0.375rem;
}
.comment-container .comment-type-icon {
  color: var(--bs-body-color);
  font-size: 0.875rem;
}
.comment-container .comment-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  line-height: 22px;
}
.comment-container .comment-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
}
.comment-container .comment-author {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--bs-body-color);
}
.comment-container .comment-action {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--bs-secondary-color);
}
.comment-container .comment-timestamp {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--bs-secondary-color);
  margin-left: auto;
}
.comment-container .comment-source {
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
  line-height: 18px;
  border-radius: 4px;
  /*--bs-bg-opacity: 0.1;*/
  background-color: var(--bs-light);
  font-weight: 500;
  font-size: 0.75rem;
  padding: 0 0.375rem;
  align-self: start;
}
.comment-container .comment-message {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--bs-body-color);
  padding: 0.25rem 0.375rem;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background-color: var(--bs-light);
}
</style>
